import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { Wordmark } from '@/components/Wordmark';
import { useAuth } from '@/features/auth/AuthProvider';
import { APP_TARGET } from '@/lib/target';
import { resolveSso } from './api';
import { GoogleButton } from './GoogleButton';

// SSO + self-serve sign-up exist only on the cloud/pi targets; embedded boards
// have a single local operator identity.
const CLOUD = APP_TARGET !== 'embedded';

const DEV_EMAIL = 'sharan@rhia.in';
const DEV_PASSWORD = 'Dev123!';

export function LoginPage() {
  const { state, login, loginWithSso } = useAuth();
  const location = useLocation() as { state?: { from?: string } };
  const [email, setEmail] = useState(import.meta.env.DEV ? DEV_EMAIL : '');
  const [password, setPassword] = useState(import.meta.env.DEV ? DEV_PASSWORD : '');
  // On cloud/pi: start at 'email' step (email-first SSO routing).
  // On embedded: skip straight to 'password' (classic direct form).
  const [step, setStep] = useState<'email' | 'password'>(CLOUD ? 'email' : 'password');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (state.status === 'authenticated') {
    return <Navigate to={location.state?.from ?? '/'} replace />;
  }

  async function handleContinue(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const provider = await resolveSso(email);
      if (provider) {
        await loginWithSso(provider);
        // signInWithRedirect navigates away; no further state update needed.
      } else {
        setStep('password');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'sign-in failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'sign-in failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto mt-4 max-w-md rounded-lg border border-[color:var(--color-border)] bg-card p-4 shadow-sm sm:mt-12 sm:p-6">
      <div className="mb-4 flex items-center gap-3">
        <Wordmark className="h-8 w-auto text-[color:var(--color-primary)] sm:h-10" />
        <div>
          <h1 className="text-lg font-bold leading-tight sm:text-xl">Sign in to RHIA Flow</h1>
          <p className="text-xs text-[color:var(--color-muted-foreground)]">
            Industrial automation control plane
          </p>
        </div>
      </div>

      {/* ── Email step (cloud/pi only): collect email, resolve SSO ── */}
      {CLOUD && step === 'email' ? (
        <form onSubmit={handleContinue} className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="mt-1 w-full rounded border border-[color:var(--color-muted)] px-2 py-1 text-sm font-mono"
              placeholder="you@example.com"
              autoComplete="email"
              // biome-ignore lint/a11y/noAutofocus: first field on a dedicated login page
              autoFocus
            />
          </label>
          {error ? (
            <p className="text-sm text-[color:var(--color-danger)]" data-testid="login-error">
              {error}
            </p>
          ) : null}
          <Button
            type="submit"
            disabled={submitting || !email}
            data-testid="login-continue"
            className="w-full"
          >
            {submitting ? 'Checking…' : 'Continue'}
          </Button>
        </form>
      ) : (
        /* ── Password step (cloud/pi) or direct form (embedded) ── */
        <>
          {CLOUD ? (
            <>
              <div className="space-y-2">
                <GoogleButton mode="login" onError={setError} />
              </div>
              <div className="my-4 flex items-center gap-3 text-xs text-[color:var(--color-muted-foreground)]">
                <span className="h-px flex-1 bg-[color:var(--color-border)]" />
                or
                <span className="h-px flex-1 bg-[color:var(--color-border)]" />
              </div>
            </>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Embedded shows the email field; cloud already collected it in step 1. */}
            {!CLOUD ? (
              <label className="block">
                <span className="text-sm font-medium">Email</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="mt-1 w-full rounded border border-[color:var(--color-muted)] px-2 py-1 text-sm font-mono"
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </label>
            ) : null}
            <label className="block">
              <span className="text-sm font-medium">Password</span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="mt-1 w-full rounded border border-[color:var(--color-muted)] px-2 py-1 text-sm font-mono"
                autoComplete="current-password"
              />
            </label>
            {error ? (
              <p className="text-sm text-[color:var(--color-danger)]" data-testid="login-error">
                {error}
              </p>
            ) : null}
            <Button
              type="submit"
              disabled={submitting || !email || !password}
              data-testid="login-submit"
              className="w-full"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          {CLOUD ? (
            <div className="mt-4 space-y-2">
              <p className="text-center text-xs text-[color:var(--color-muted-foreground)]">
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setError(null);
                  }}
                  data-testid="login-change-email"
                  className="font-semibold text-[color:var(--color-primary)] hover:underline"
                >
                  ← Use a different email
                </button>
              </p>
              <p className="text-center text-xs text-[color:var(--color-muted-foreground)]">
                New to RHIA Flow?{' '}
                <Link
                  to="/signup"
                  data-testid="login-signup-link"
                  className="font-semibold text-[color:var(--color-primary)] hover:underline"
                >
                  Create an account
                </Link>
              </p>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
